import { type ReactElement } from 'react';
import { CalendarArrowUp, Info, UserRoundArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import { getFamilyMemberFullName } from '@/features/families/mappers';
import { useFamilyDetailMutations } from '@/features/family-record/hooks/useFamilyDetailMutations';
import { PedigreeNameEditor } from '@/features/pedigree/PedigreeNameEditor';
import type {
  CascadeContactStatus,
  FamilyMemberViewModel,
  FamilyViewModel,
} from '@/models/family';

interface FamilySidePanelsProps {
  readonly family: FamilyViewModel;
}

/**
 * Right rail for the family record. Same visual idiom as
 * `PatientSidePanels` (320px column of small cards) but with
 * family-appropriate content. Order (PTV2-1157): Info (name + member
 * count) → Proband snapshot → Cascade contact counts.
 */
export function FamilySidePanels({ family }: FamilySidePanelsProps): ReactElement {
  return (
    <aside className="flex flex-col gap-3">
      <FamilyInfoPanel family={family} />
      <ProbandSnapshotPanel family={family} />
      <CascadeStatsPanel members={family.members} />
    </aside>
  );
}

/**
 * Info widget (PTV2-1157) — replaces the removed Overview section as the home
 * for the family name (editable) and member count. The name uses the same
 * pencil-wrapped editor as the pedigree name: click the pencil to edit, then
 * the checkmark to save via `PATCH /families/{guid}`. The BE rejects a blank
 * name, so an empty value is ignored on save.
 */
function FamilyInfoPanel({ family }: { readonly family: FamilyViewModel }): ReactElement {
  const { t } = useTranslation();
  const { patch } = useFamilyDetailMutations(family.guid);

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
        <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {t('routes.family.sidePanels.info.heading')}
        </h2>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="space-y-1">
          <span className="block text-sm text-foreground/85">
            {t('routes.family.sidePanels.info.name')}
          </span>
          <PedigreeNameEditor
            value={family.name}
            saving={patch.isPending}
            onSave={(next) => {
              // The BE rejects a blank name — ignore an empty save (the field reverts to the old value).
              if (next.length > 0 && next !== family.name) {
                patch.mutate({ name: next });
              }
            }}
            placeholder={t('routes.family.unnamedFamily')}
            emptyLabel={t('routes.family.unnamedFamily')}
            ariaLabel={t('routes.family.sidePanels.info.name')}
            className="w-full"
            valueClassName="flex-1"
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-foreground/85">
            {t('routes.family.sidePanels.info.memberCount')}
          </span>
          <span className="text-sm font-medium tabular-nums text-foreground">
            {family.members.length.toLocaleString()}
          </span>
        </div>
      </div>
    </Card>
  );
}

const STATUS_ORDER: readonly CascadeContactStatus[] = [
  'NOT_CONTACTED',
  'CONTACTED',
  'TESTED',
  'DECLINED',
];

function CascadeStatsPanel({
  members,
}: {
  readonly members: readonly FamilyMemberViewModel[];
}): ReactElement {
  const { t } = useTranslation();
  const counts: Record<CascadeContactStatus, number> = {
    NOT_CONTACTED: 0,
    CONTACTED: 0,
    TESTED: 0,
    DECLINED: 0,
  };
  for (const m of members) {
    counts[m.contactStatus] += 1;
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
        <CalendarArrowUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {t('routes.family.sidePanels.cascade.heading')}
        </h2>
      </div>
      <dl className="divide-y divide-border">
        {STATUS_ORDER.map((status) => (
          <div key={status} className="px-4 py-2 flex items-center justify-between gap-3">
            <dt className="text-sm text-foreground/85">
              {t(`family.contactStatus.${status}`)}
            </dt>
            <dd className="text-sm font-medium tabular-nums text-foreground">
              {counts[status].toLocaleString()}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function ProbandSnapshotPanel({
  family,
}: {
  readonly family: FamilyViewModel;
}): ReactElement {
  const { t } = useTranslation();
  const proband =
    family.probandGuid === null
      ? null
      : (family.members.find((m) => m.cascadeRole === 'PROBAND') ?? null);
  const probandName = proband !== null ? getFamilyMemberFullName(proband) : null;

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
        <UserRoundArrowLeft className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {t('routes.family.sidePanels.proband.heading')}
        </h2>
      </div>
      <div className="px-4 py-3 text-sm">
        {family.probandGuid === null ? (
          <p className="text-muted-foreground">
            {t('routes.family.sidePanels.proband.none')}
          </p>
        ) : (
          <div className="space-y-1">
            <p className="text-foreground font-medium">
              {probandName ?? t('routes.family.sidePanels.proband.unnamed')}
            </p>
            <Link
              to={`/patients/${family.probandGuid}`}
              className="text-primary text-xs hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              {t('routes.family.sidePanels.proband.openRecord')}
            </Link>
          </div>
        )}
      </div>
    </Card>
  );
}
